import {
  Component,
  OnInit,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ViewChildren,
  ElementRef,
  AfterViewInit,
  HostListener,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  IngredientChoiceGroup,
  IngredientChoicesType,
  IngredientChoice,
  OrderedItem,
  OrderedItemService,
  OrderableItem,
} from 'ngx-web-api';
import { IngredientSelection } from '../../domain/ingredient-selection';
import { StageIngredientAction } from '../../item-editor/domain/stage-ingredient-action.model';
import { Animations } from '../../animations';
import { Subscription } from 'rxjs';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { ViewportService } from 'app/core/services/viewport.service';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { ViewPort } from 'app/domain/view-port.model';
import { ItemEditorService } from '../../core/services/item-editor.service';
import { faAngleLeft, faAngleRight as faAngleRightLight } from '@fortawesome/pro-light-svg-icons';
import { CanMoveToStepGroupPipe } from '../pipes/can-move-to-step-group.pipe';
import { GroupIndexPipe } from '../pipes/group-index.pipe';
import { HighlightElementsService } from '../highlight-elements.service';
import { EditingItemChoicesService } from 'app/core/services/editing-item-choices.service';
import { FocusService } from 'app/focus.service';

@Component({
  selector: 'fts-choice-groups',
  templateUrl: 'choice-groups.component.html',
  styleUrls: ['choice-groups.component.scss'],
  animations: [Animations.expanded],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChoiceGroupsComponent implements OnInit, AfterViewInit, OnChanges {
  @Input()
  choiceGroups: IngredientChoiceGroup[];
  @Input()
  selectedChoiceGroup: IngredientChoiceGroup;
  @Input()
  nextChoiceGroup: IngredientChoiceGroup;
  @Input()
  previousChoiceGroup: IngredientChoiceGroup;
  @Input()
  ingredientChoicesType: IngredientChoicesType;
  @Input()
  choices: IngredientChoice[] = [];
  @Input()
  orderableChoices: IngredientChoice[] = [];
  @Input()
  selectedChoice: IngredientChoice;
  @Input()
  item: OrderedItem;
  @Input()
  price: number;
  @Input()
  caloriesDisclaimer: string;
  @Input()
  hasGraphicalBuilder: boolean;
  @Input()
  inModal: boolean;
  @Input()
  submitBtnLabel: string;
  @Input()
  submitLoadingSubscription: Subscription;
  @Input()
  orderableItem: OrderableItem;
  @Input()
  inItemEditorModal = false;
  @Input()
  isHalfAndHalf = true;

  @Output()
  choiceGroupChange = new EventEmitter<IngredientChoiceGroup>();
  @Output()
  choiceChange = new EventEmitter<IngredientChoice>();
  @Output()
  halfSelectionChange = new EventEmitter<IngredientSelection>();
  @Output()
  qualifierSelectionChange = new EventEmitter<IngredientSelection>();
  @Output()
  ingredientSelectionsChange = new EventEmitter<IngredientSelection[]>();
  @Output()
  stageActionChange: EventEmitter<StageIngredientAction> = new EventEmitter();
  @Output()
  submitBtnClick = new EventEmitter<void>();
  @Output()
  cancelBtnClick = new EventEmitter<void>();

  ingredientChoicesTypeEnum = IngredientChoicesType;
  leftStepsWrapperPosition: string;
  faAngleRight = faAngleRight;
  isFirstChoiceGroup: boolean;
  isLastChoiceGroup: boolean;
  isMobile = false;
  filteredGroups: IngredientChoiceGroup[];
  faAngleLeft = faAngleLeft;
  faAngleRightLight = faAngleRightLight;

  shouldShowBackBtn: boolean;
  shouldShowForwardBtn: boolean;
  shouldShowSubmitBtn: boolean;

  groupIndexPipe = new GroupIndexPipe();
  canMoveToStepGroupPipe = new CanMoveToStepGroupPipe();
  filteredListGroups: IngredientChoiceGroup[] = [];

  @ViewChildren('scrollLink')
  private scrollLinks;
  @ViewChild('scrollLinksWrapper', { static: false })
  private scrollLinksWrapper: ElementRef;
  @ViewChild('stepsWrapper', { static: false })
  private stepsWrapper: ElementRef;

  constructor(
    private viewportService: ViewportService,
    private itemEditorService: ItemEditorService,
    private orderedItemService: OrderedItemService,
    private highlightService: HighlightElementsService,
    private editingService: EditingItemChoicesService,
    private focusService: FocusService
  ) {}

  @HostListener('window:resize')
  onResize() {
    this.calculateLeftStepsWrapperPosition();
  }

  ngOnInit() {
    this.viewportService.viewport$
      .pipe(
        map((viewport: ViewPort) => viewport.isMobile),
        distinctUntilChanged()
      )
      .subscribe((isMobile: boolean) => (this.isMobile = isMobile));
    this.initListGroupChoices();
  }

  ngAfterViewInit() {
    this.calculateLeftStepsWrapperPosition();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] || changes['choices']) {
      this.filteredGroups = this.getFilteredChoiceGroups();
      if (changes['choices'].previousValue?.length !== changes['choices'].currentValue?.length) {
        this.initListGroupChoices(true);
      }
      this.calcShouldShowBackBtn();
    }
    if (changes['selectedChoiceGroup']) {
      if (this.ingredientChoicesType === this.ingredientChoicesTypeEnum.Steps) {
        this.animateChoiceGroupStep();
        this.isFirstChoiceGroup = this.choiceGroupIndex(this.selectedChoiceGroup) === 0;
        this.isLastChoiceGroup = this.choiceGroupIndex(changes.selectedChoiceGroup.currentValue) === this.choiceGroups.length - 1;
      }
    }
  }

  onLoadItemEditorModalHeaderComponent({ scrollLinksWrapper, scrollLinks }) {
    this.scrollLinksWrapper = scrollLinksWrapper;
    this.scrollLinks = scrollLinks;
  }

  trackByChoiceGroupFn(_index, choiceGroup: IngredientChoiceGroup) {
    return choiceGroup.name;
  }

  isFirstChoiceGroupSelected(): boolean {
    const firstChoiceGroup = this.getFilteredChoiceGroups()[0];
    return this.selectedChoiceGroup && this.selectedChoiceGroup.name === firstChoiceGroup?.name;
  }

  isLastChoiceGroupSelected(): boolean {
    const lastChoiceGroup = this.getFilteredChoiceGroups().slice(-1)[0];
    return this.selectedChoiceGroup && this.selectedChoiceGroup.name === lastChoiceGroup?.name;
  }

  moveToNextChoiceGroup() {
    setTimeout(() => this.focusService.applyFocus('choiceGroup'), 200);
    if (this.item.hasInvalidChoiceFor(this.selectedChoiceGroup)) {
      this.editingService.selectFirstInvalidChoice(this.orderableItem, this.item);
      this.highlightService.highLightAll(true);
      return;
    }
    if (this.nextChoiceGroup) {
      this.onChoiceGroupChange(this.nextChoiceGroup);
    }
  }

  moveToPreviousChoiceGroup() {
    setTimeout(() => this.focusService.applyFocus('choiceGroup'), 200);
    if (this.previousChoiceGroup && !this.isFirstChoiceGroupSelected()) {
      this.onChoiceGroupChange(this.previousChoiceGroup);
    }
  }

  onChoiceGroupChange(choiceGroup: IngredientChoiceGroup, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (
      this.ingredientChoicesType !== this.ingredientChoicesTypeEnum.Steps ||
      this.canMoveToStepGroupPipe.transform(choiceGroup, this.item, this.selectedChoiceGroup, this.choiceGroups)
    ) {
      this.choiceGroupChange.emit(choiceGroup);
    }
  }

  onChangeChoiceGroup(choiceGroup: IngredientChoiceGroup) {
    this.choiceGroupChange.emit(choiceGroup);
  }

  onChoiceGroupToggle(choiceGroup: IngredientChoiceGroup, event: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.ingredientChoicesType === this.ingredientChoicesTypeEnum.List) {
      choiceGroup.isExpanded = !choiceGroup.isExpanded;
    }
    if (this.selectedChoiceGroup && this.selectedChoiceGroup.name === choiceGroup.name) {
      this.choiceGroupChange.emit(undefined);
    } else {
      this.choiceGroupChange.emit(choiceGroup);
    }
  }

  onChoiceChange(choice: IngredientChoice, choiceGroupName?: string) {
    if (!!choiceGroupName && this.ingredientChoicesType === this.ingredientChoicesTypeEnum.List) {
      this.filteredListGroups.find(g => g.name === choiceGroupName).selectedChoice = choice;
    }
    this.choiceChange.emit(choice);
  }

  onHalfSelectionChange(ingredientSelection: IngredientSelection) {
    this.halfSelectionChange.emit(ingredientSelection);
  }

  onQualifierSelectionChange(ingredientSelection: IngredientSelection) {
    this.qualifierSelectionChange.emit(ingredientSelection);
  }

  onIngredientSelectionsChange(selections: IngredientSelection[]) {
    this.ingredientSelectionsChange.emit(selections);
  }

  onStageActionChange(stageAction: StageIngredientAction) {
    this.stageActionChange.emit(stageAction);
  }

  onSubmitBtnClick() {
    if (this.item.hasInvalidChoiceFor(this.selectedChoiceGroup)) {
      this.highlightService.highLightAll(true);
      return;
    }
    this.submitBtnClick.emit();
  }

  onCancelBtnClick() {
    this.cancelBtnClick.emit();
  }

  private choiceGroupIndex(choiceGroup: IngredientChoiceGroup) {
    return this.groupIndexPipe.transform(choiceGroup, this.choiceGroups);
  }

  private calcShouldShowBackBtn() {
    this.shouldShowBackBtn =
      this.ingredientChoicesType === this.ingredientChoicesTypeEnum.Steps &&
      !this.isFirstChoiceGroupSelected() &&
      this.filteredGroups.length > 0;
    this.shouldShowForwardBtn =
      this.ingredientChoicesType === this.ingredientChoicesTypeEnum.Steps &&
      !this.isLastChoiceGroupSelected() &&
      this.filteredGroups.length > 0;
    this.shouldShowSubmitBtn =
      this.isLastChoiceGroupSelected() ||
      this.filteredGroups.length === 0 ||
      this.ingredientChoicesType !== this.ingredientChoicesTypeEnum.Steps;
  }

  private animateChoiceGroupStep() {
    const elementRef =
      this.selectedChoiceGroup && this.scrollLinks
        ? this.scrollLinks.toArray()[this.choiceGroupIndex(this.selectedChoiceGroup)]
        : undefined;

    if (elementRef) {
      this.scrollLinksWrapper.nativeElement.scrollTo({ top: 0, left: elementRef.nativeElement.offsetLeft, behavior: 'smooth' });
    }
  }

  private calculateLeftStepsWrapperPosition() {
    if (this.ingredientChoicesType === this.ingredientChoicesTypeEnum.Steps && !this.isMobile) {
      this.leftStepsWrapperPosition = `${this.stepsWrapper?.nativeElement.getBoundingClientRect().left}px`;
    }
  }

  private getFilteredChoiceGroups(): IngredientChoiceGroup[] {
    return this.itemEditorService.filterChoiceGroupsByDependency(this.choiceGroups, this.orderableChoices, this.item).filter(group => {
      if (!!group.choices) {
        return group.choices
          .map(choiceName => {
            const choice = this.orderableChoices.find(c => c.name === choiceName);
            return this.choiceHasIngredients(choice);
          })
          .reduce((a, b) => a || b, false);
      }
    });
  }

  private choiceHasIngredients(choice: IngredientChoice): boolean {
    return choice?.ingredients?.some(i => !!i.getSizePrice(this.item.size)) && this.orderedItemService.hasDependencyMet(this.item, choice);
  }

  private initListGroupChoices(updateDependingChoices = false) {
    if (this.ingredientChoicesType !== this.ingredientChoicesTypeEnum.List) {
      return;
    }
    const expandedMap = updateDependingChoices ? this.filteredListGroups.map(fg => ({ isExpanded: fg.isExpanded, name: fg.name })) : null;
    this.filteredListGroups = this.filteredGroups.map(choiceGroup => {
      const choiceGroupCopy = choiceGroup.copy();
      if (!choiceGroupCopy.isExpanded) {
        choiceGroupCopy.isExpanded =
          this.selectedChoiceGroup?.name === choiceGroupCopy.name || this.item.hasInvalidChoiceFor(choiceGroupCopy);
      }
      choiceGroupCopy.ingredientChoices = this.choices.filter(c => choiceGroupCopy.choices.includes(c.name));
      choiceGroupCopy.selectedChoice = choiceGroupCopy.ingredientChoices[0];

      if (updateDependingChoices && !!expandedMap) {
        choiceGroupCopy.isExpanded = expandedMap.find(cg => cg.name === choiceGroupCopy.name)?.isExpanded ?? choiceGroupCopy.isExpanded;
      }

      return choiceGroupCopy;
    });
  }
}
